import { Check } from 'lucide-react';
import { Checkbox as CheckboxPrimitive } from 'radix-ui';
import * as React from 'react';

import { cn } from '@/_shared/lib/utils';

type CheckboxSize = 'md' | 'lg';

const SIZE_CLASSES: Record<CheckboxSize, { box: string; check: string }> = {
  md: { box: 'size-4 rounded-[0.25rem]', check: 'size-3' },
  lg: { box: 'size-5 rounded-md', check: 'size-4' },
};

/**
 * Checkbox (built on the unified `radix-ui` Checkbox, restyled to Lookout tokens): `border-input`
 * when unchecked, `bg-primary` with a `primary-foreground` check when checked, and an offset focus
 * ring on keyboard focus (matching Button/Switch/Radio). The ring is spelled out with explicit
 * `focus-visible:ring-*` classes (rather than the `focus-ring` utility) so a row-level pattern can
 * suppress it via `focus-visible:ring-0 focus-visible:ring-offset-0` — the whole row shows the ring
 * instead (see the multi-select option rows). `size` md = 16px · lg = 20px.
 */
function Checkbox({
  className,
  size = 'md',
  ...props
}: React.ComponentProps<typeof CheckboxPrimitive.Root> & { size?: CheckboxSize }): React.ReactElement {
  return (
    <CheckboxPrimitive.Root
      className={cn(
        'flex shrink-0 items-center justify-center border border-input bg-background transition-colors',
        SIZE_CLASSES[size].box,
        'focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-none',
        'data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator className="flex items-center justify-center">
        <Check className={SIZE_CLASSES[size].check} strokeWidth={3.5} />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
}

export { Checkbox };
